import { z } from 'zod';
import { centAmount, isoDate, positiveAmount, uuid } from '../posting.js';
import { buchungsseiteSchema } from '../datev/zerlegung.js';
/**
 * Die Leseverträge des Geldkonto-Anfangsbestands — Auftrag
 * `K-geldkontenmaske` (2026-09-08), Migration `0198` (`K-geldkontenanfang`),
 * `E-1055`.
 *
 * **Warum eine eigene Datei und nicht ein Absatz in `kontierung.ts`.** Die
 * Endpunkte selbst (`GeldkontoAnfangsbestandController`,
 * `geldkontenanfang.controller.ts`) stehen unter `api/v1/kontierungen`, aber
 * die Sache — ein Eröffnungssaldo, aus einem Kontoauszug abgeleitet, von einer
 * zweiten Person freigegeben — hat mit den übrigen Verträgen dieser Datei
 * (Kontierungspositionen, Herkunft, Vorschlag) fachlich nichts gemeinsam.
 * Dieselbe Erwägung wie bei `steuer/dienst.ts` neben `documents.ts`.
 *
 * **Nur Leseverträge, kein Schreibvertrag für die Anlage.** Die Anlage
 * geschieht ausschliesslich über den Handlauf
 * `pnpm kontierung:geldkontenanfang -- --run` und nimmt keinen Rumpf aus dem
 * Netz entgegen (Kopfkommentar `geldkontenanfang.controller.ts`, „KEIN `POST`
 * ZUM ANLEGEN"). Die Freigabe (`POST …/freigabe`) nimmt ebenfalls keinen
 * Rumpf — welche Eröffnung freigegeben wird, sagt der Pfad.
 */
/** Die Richtung, in der das Geldkonto steht — dasselbe Schema wie `buchungsseiteSchema` (DATEV-Zerlegung), weil es dieselben zwei Werte sind. */
export const geldkontoAnfangsbestandRichtungSchema = buchungsseiteSchema;
/** Der Zustand einer Zeile — `geldkonto_anfangsbestand_status_bekannt` (Migration `0198`). */
export const geldkontoAnfangsbestandStatusSchema = z.enum(['angelegt', 'freigegeben']);
/** Eine Kontonummer — dieselbe Form wie `postingLineSchema.accountNumber`. */
const kontonummer = z.string().regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern');
/**
 * Ein Eintrag der Arbeitsliste `GET …/kontierungen/geldkonto-anfangsbestaende`
 * — je Geldkonto der aktuelle Stand.
 *
 * Spiegelt `GeldkontoAnfangsbestandZeile` in
 * `apps/api/src/kontierung/geldkontenanfang.controller.ts` Feld für Feld;
 * jenes Interface bleibt die Quelle, dieses Schema ihre Probe.
 *
 * **`quelleSaldo` und `anschluss` sind vorzeichenbehaftet** (`centAmount`,
 * nicht `positiveAmount`) — ein überzogenes Bankkonto oder rückläufige
 * Anschlussbewegungen sind gemessene, keine Ausnahmefälle (Kopfkommentar
 * `geldkontenanfang.ts`, Beispiel `1210`: Anschluss −490,56 €). `betrag`
 * selbst ist dagegen echt positiv — die Richtung trägt das Vorzeichen
 * (`geldkonto_anfangsbestand_betrag_positiv`).
 */
export const geldkontoAnfangsbestandZeileSchema = z
    .object({
    id: uuid,
    zeileId: uuid,
    geldkonto: kontonummer,
    gegenkonto: kontonummer,
    stichtag: isoDate,
    buchungsdatum: isoDate,
    richtung: geldkontoAnfangsbestandRichtungSchema,
    betrag: positiveAmount,
    quelleBis: isoDate.nullable(),
    quelleSaldo: centAmount,
    anschluss: centAmount,
    begruendung: z.string(),
    status: geldkontoAnfangsbestandStatusSchema,
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): Wer die Eröffnung angelegt hat, hat sie
     * auch freigegeben — nur unter einer Freigaberegel ohne vier Augen.
     * `optional()`, weil ältere Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
})
    .strict();
/** `GET …/kontierungen/geldkonto-anfangsbestaende` — die volle Antwort. */
export const geldkontoAnfangsbestandListeSchema = z
    .object({
    anfangsbestaende: z.array(geldkontoAnfangsbestandZeileSchema),
})
    .strict();
/**
 * `POST …/kontierungen/geldkonto-anfangsbestaende/:id/freigabe` — die
 * Antwort. Kein Rumpf auf der Anfrageseite (der Pfad sagt, was freigegeben
 * wird); die Antwort nennt die neue Zeile und die entstandene Buchung.
 */
export const geldkontoAnfangsbestandFreigabeAntwortSchema = z
    .object({
    id: uuid,
    journalEntryId: uuid,
})
    .strict();
//# sourceMappingURL=geldkonto-anfangsbestand.js.map